import React, { useMemo } from 'react';
import { Button, Dropdown, Empty, Menu, Spin, Typography, type MenuProps } from 'antd';
import { LoadingOutlined, MoreOutlined } from '@ant-design/icons';
import { history, useLocation } from '@umijs/max';
import './historyList.less';
import type { SessionHistoryItem } from '../types';
import { getConversationItemKey, getSessionProductPath } from '../util';

interface HistoryListProps {
  items: SessionHistoryItem[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onRename: (item: SessionHistoryItem) => void;
  onDelete: (item: SessionHistoryItem) => void;
  emptyText?: string;
}

const ACTION_ITEMS: MenuProps['items'] = [
  {
    key: 'rename',
    label: '重命名',
  },
  {
    type: 'divider',
  },
  {
    key: 'delete',
    label: '删除',
    danger: true,
  },
];

const getActionMenu = (item: SessionHistoryItem, onRename: (target: SessionHistoryItem) => void, onDelete: (target: SessionHistoryItem) => void) => ({
  items: ACTION_ITEMS,
  onClick: ({ key, domEvent }: Parameters<NonNullable<MenuProps['onClick']>>[0]) => {
    domEvent.stopPropagation();
    if (key === 'rename') {
      onRename(item);
    }
    if (key === 'delete') {
      onDelete(item);
    }
  },
});

const skipToProduct = (item: SessionHistoryItem) => {
  const path = getSessionProductPath(item);
  if (path) {
    history.push(path);
  }
};

const renderLoadingLabel = () => (
  <div className="session-history-list-item-loading">
    <LoadingOutlined className="session-history-list-item-loading-icon" spin />
  </div>
);

const HistoryList: React.FC<HistoryListProps> = ({ items, loading = false, loadingMore = false, hasMore = false, onRename, onDelete, emptyText = '暂无历史会话' }) => {
  const location = useLocation();
  const activeSessionId = useMemo(() => new URLSearchParams(location.search).get('session_id')?.trim() || '', [location.search]);

  const itemMap = useMemo(() => {
    const map = new Map<string, SessionHistoryItem>();
    items.forEach((item) => {
      const key = getConversationItemKey(item);
      if (key) {
        map.set(key, item);
      }
    });
    return map;
  }, [items]);

  const menuItems = useMemo<MenuProps['items']>(() => {
    return items.map((item) => {
      const itemKey = getConversationItemKey(item);

      return {
        key: itemKey,
        icon: item.icon ? <img src={item.icon} alt="" width={16} height={16} /> : undefined,
        label: (
          <div className="session-history-menu-item-label">
            <Typography.Text className="session-history-menu-item-title" ellipsis={{ tooltip: item.title }}>
              {item.title}
            </Typography.Text>
            <div className="session-history-menu-item-actions">
              <div className="session-history-menu-item-status">{item.status === 'loading' ? renderLoadingLabel() : item.read_status === 'unread' ? <span className="session-history-list-item-unread" /> : null}</div>
              <div className="session-history-menu-item-more-wrap">
                <Dropdown trigger={['click']} menu={getActionMenu(item, onRename, onDelete)} placement="bottomRight">
                  <Button
                    className="session-history-menu-item-more"
                    type="text"
                    icon={<MoreOutlined />}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  />
                </Dropdown>
              </div>
            </div>
          </div>
        ),
      };
    });
  }, [items, onDelete, onRename]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const item = itemMap.get(String(key));
    if (item) {
      skipToProduct(item);
    }
  };

  if (loading) {
    return (
      <div className="session-history-list session-history-list-loading">
        <Spin indicator={<LoadingOutlined className="session-history-list-loading-icon" spin />} />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="session-history-empty">
        <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <div className="session-history-list">
      <Menu className="session-history-menu" mode="inline" selectable selectedKeys={activeSessionId ? [activeSessionId] : []} items={menuItems} onClick={handleMenuClick} />
      {loadingMore ? (
        <div className="session-history-list-load-more">
          <LoadingOutlined spin />
          <span>加载中...</span>
        </div>
      ) : null}
      {!loadingMore && !hasMore && items.length > 0 ? <div className="session-history-list-load-more session-history-list-load-more--end">没有更多了</div> : null}
    </div>
  );
};

export default HistoryList;
